import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  SpinWheelService,
  SpinCampaign,
  SpinSliceInfo,
  SubmitSpinOutcomePayload
} from '../../services/spin-wheel.service';
import { StaticMemory } from '../../handler/static_memory';
import { SnackBarHandler } from 'src/app/other/open-snack-bar';

export interface SpinWheelReward {
  _id?: string;
  campaignId?: string;
  couponId?: string;
  label: string;
  subLabel?: string;
  code?: string;
  color: string;
  textColor: string;
  discount: string;
  icon: string; // 'tag' | 'truck' | 'refresh' | 'gift' | 'percent'
  weight?: number;
  couponInfo?: any;
  couponType?: string;
  resultTitle?: string;
  resultSubtitle?: string;
  resultMessage?: string;
  isLoss?: boolean;
}

@Component({
  selector: 'app-spin-wheel-dialog',
  templateUrl: './spin-wheel-dialog.component.html',
  styleUrls: ['./spin-wheel-dialog.component.scss'],
  standalone: false
})
export class SpinWheelDialogComponent implements OnInit {
  campaign: SpinCampaign | null = null;
  warehouseId: string = '';
  loading = false;
  submittingOutcome = false;
  alreadyUsed = false;
  errorMessage = '';
  rewards: SpinWheelReward[] = [];
  rawSlices: SpinSliceInfo[] = [];

  isSpinning = false;
  hasSpun = false;
  rotationDegree = 0;
  wonReward: SpinWheelReward | null = null;
  copied = false;

  private getSliceColor(index: number, total: number): string {
    if (total <= 0) return '#EA580C';
    if (total % 2 === 0) {
      return index % 2 === 0 ? '#EA580C' : '#F89634';
    }
    if (total === 3) {
      const palette3 = ['#EA580C', '#F89634', '#D97706'];
      return palette3[index % 3];
    }
    if (index === total - 1) {
      return '#D97706';
    }
    return index % 2 === 0 ? '#EA580C' : '#F89634';
  }

  constructor(
    public dialogRef: MatDialogRef<SpinWheelDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private spinWheelService: SpinWheelService,
    private snackBar: SnackBarHandler
  ) {}

  ngOnInit(): void {
    this.warehouseId = this.data?.warehouseId || StaticMemory.serAreaObject?.warehouseId || '';

    if (this.data?.campaign) {
      this.initCampaign(this.data.campaign);
    } else {
      this.fetchActiveCampaign();
    }
  }

  fetchActiveCampaign(): void {
    this.loading = true;
    this.spinWheelService.getActiveCampaigns(this.warehouseId).subscribe({
      next: (res) => {
        this.loading = false;
        if (res && res.status && res.result && res.result.length > 0) {
          const activeCampaign = res.result[0];
          this.initCampaign(activeCampaign);
        } else {
          this.rewards = [];
          this.rawSlices = [];
        }
      },
      error: (err) => {
        this.loading = false;
        this.rewards = [];
        this.rawSlices = [];
      }
    });
  }

  initCampaign(campaign: SpinCampaign): void {
    this.campaign = campaign;
    if (campaign.used) {
      this.alreadyUsed = true;
    }

    if (campaign.sliceInfo && campaign.sliceInfo.length >= 3) {
      this.rawSlices = campaign.sliceInfo;
      this.rewards = this.mapSlicesToRewards(campaign.sliceInfo);
    } else {
      this.rewards = [];
      this.rawSlices = [];
    }
  }

  private mapSlicesToRewards(slices: SpinSliceInfo[]): SpinWheelReward[] {
    return slices.map((slice, index) => {
      const labelText = (slice.sliceLabel || '').trim();
      const parts = labelText.split(/\s+/);
      let label = labelText;
      let subLabel: string | undefined = undefined;

      if (slice.subLabel) {
        label = slice.sliceLabel || '';
        subLabel = slice.subLabel;
      } else if (parts.length === 2) {
        label = parts[0];
        subLabel = parts[1];
      } else if (parts.length > 2) {
        const mid = Math.ceil(parts.length / 2);
        label = parts.slice(0, mid).join(' ');
        subLabel = parts.slice(mid).join(' ');
      }

      const couponInfo = slice.couponInfo || {};
      const couponType = (couponInfo.type || '').toUpperCase();
      const lower = labelText.toLowerCase();

      let icon = slice.icon || 'tag';
      if (couponType === 'FREE-SHIPPING' || lower.includes('ship') || lower.includes('delivery')) {
        icon = 'truck';
      } else if (
        couponType === 'BETTER-LUCK-NEXT-TIME' ||
        lower.includes('try') ||
        lower.includes('again') ||
        lower.includes('luck') ||
        lower.includes('spin')
      ) {
        icon = 'refresh';
      } else if (lower.includes('gift') || lower.includes('box') || lower.includes('present')) {
        icon = 'gift';
      } else if (
        couponInfo.discountType === 'CENT' ||
        couponInfo.discountType === 'PERCENT' ||
        lower.includes('%') ||
        lower.includes('percent') ||
        lower.includes('off')
      ) {
        icon = 'percent';
      }

      let isLoss = false;
      let resultTitle = 'Congratulations!';
      let resultSubtitle = 'You won';
      let resultMessage = '';
      let discountDisplay = slice.discount || slice.sliceLabel || 'Special Reward';

      if (
        couponType === 'BETTER-LUCK-NEXT-TIME' ||
        couponType === 'TRY-AGAIN' ||
        couponType === 'NO-REWARD' ||
        lower.includes('better luck') ||
        lower.includes('try again') ||
        lower.includes('luck next time')
      ) {
        isLoss = true;
        resultTitle = 'Better Luck Next Time!';
        resultSubtitle = "Don't give up!";
        discountDisplay = slice.sliceLabel || 'Better Luck Next Time';
        resultMessage =
          couponInfo.description &&
          couponInfo.description !== 'Better Luck' &&
          couponInfo.description !== 'sorry'
            ? couponInfo.description
            : 'Check back in the next period to test your luck again!';
      } else if (
        couponType === 'FREE-SHIPPING' ||
        lower.includes('ship') ||
        lower.includes('delivery')
      ) {
        isLoss = false;
        resultTitle = 'Congratulations!';
        resultSubtitle = 'You won';
        discountDisplay = 'Free Shipping';
        resultMessage =
          couponInfo.minOrderValue > 0
            ? `Valid on orders above ₹${couponInfo.minOrderValue}`
            : 'Free shipping applicable on your next order';
      } else if (
        couponType === 'CASHBACK' ||
        (couponInfo.cashbackAmount && couponInfo.cashbackAmount > 0)
      ) {
        isLoss = false;
        resultTitle = 'Congratulations!';
        resultSubtitle = 'You won';
        const cbVal = couponInfo.cashbackAmount || 0;
        discountDisplay = cbVal > 0 ? `₹${cbVal} Cashback` : slice.sliceLabel || 'Cashback';
        resultMessage =
          couponInfo.minOrderValue > 0
            ? `Min order value: ₹${couponInfo.minOrderValue}`
            : couponInfo.description && couponInfo.description !== 'Cashback'
              ? couponInfo.description
              : 'Cashback will be credited on your order';
      } else if (couponType === 'NORMAL' || couponInfo.discount > 0) {
        isLoss = false;
        resultTitle = 'Congratulations!';
        resultSubtitle = 'You won';
        const dType = (couponInfo.discountType || '').toUpperCase();
        if (dType === 'CENT' || dType === 'PERCENT' || dType === 'PERCENTAGE') {
          discountDisplay = `${couponInfo.discount}% OFF`;
        } else if (dType === 'FLAT') {
          discountDisplay = `₹${couponInfo.discount} FLAT OFF`;
        } else if (couponInfo.discount) {
          discountDisplay = `₹${couponInfo.discount} OFF`;
        }
        resultMessage =
          couponInfo.minOrderValue > 0
            ? `Min order value: ₹${couponInfo.minOrderValue}`
            : couponInfo.description && couponInfo.description !== 'sorry'
              ? couponInfo.description
              : 'Use this coupon on your next order';
      } else {
        isLoss = false;
        resultTitle = 'Congratulations!';
        resultSubtitle = 'You won';
        discountDisplay = slice.discount || slice.sliceLabel || 'Special Reward';
        resultMessage = couponInfo.description || '';
      }

      const color = slice.color || this.getSliceColor(index, slices.length);
      const campaignId = slice.campaignId || '';
      const code = isLoss ? '' : slice.couponCode || couponInfo.code || '';

      return {
        _id: slice._id,
        campaignId,
        couponId: slice.couponId,
        label,
        subLabel,
        code,
        color,
        textColor: '#FFFFFF',
        discount: discountDisplay,
        icon,
        weight: slice.weight || 1,
        couponInfo,
        couponType,
        resultTitle,
        resultSubtitle,
        resultMessage,
        isLoss
      };
    });
  }

  private chooseWeightedIndex(): number {
    const total = this.rewards.length;
    if (total === 0) return 0;

    const totalWeight = this.rewards.reduce((sum, r) => sum + (r.weight || 1), 0);
    let randomNum = Math.random() * totalWeight;

    for (let i = 0; i < total; i++) {
      const weight = this.rewards[i].weight || 1;
      if (randomNum < weight) {
        return i;
      }
      randomNum -= weight;
    }
    return Math.floor(Math.random() * total);
  }

  spin(): void {
    if (
      this.isSpinning ||
      this.hasSpun ||
      this.alreadyUsed ||
      this.rewards.length === 0 ||
      this.submittingOutcome
    ) {
      return;
    }

    this.isSpinning = true;
    this.submittingOutcome = true;

    const campaignId =
      this.campaign?._id ||
      this.campaign?.campaignId ||
      (this.rawSlices && this.rawSlices.length > 0 ? this.rawSlices[0].campaignId : '') ||
      '';

    const payload: SubmitSpinOutcomePayload = {
      warehouseId: this.campaign?.warehouseId || this.warehouseId,
      campaignId: campaignId
    };

    this.spinWheelService.submitSpinOutcome(payload).subscribe({
      next: (res) => {
        this.submittingOutcome = false;
        if (res && res.status) {
          let selectedIndex = this.rewards.findIndex(
            (r) =>
              (res.result?.sliceId && r._id === res.result.sliceId) ||
              (res.result?.couponId && r.couponId === res.result.couponId) ||
              (res.result?.sliceLabel && r.label === res.result.sliceLabel)
          );

          if (selectedIndex === -1) {
            selectedIndex = this.chooseWeightedIndex();
          }

          const selectedReward = this.rewards[selectedIndex];
          if (res.result?.couponCode) {
            selectedReward.code = res.result.couponCode;
          }

          // Calculate rotation angle towards server-determined slice
          const numSegments = this.rewards.length;
          const segmentAngle = 360 / numSegments;
          const extraSpins = (5 + Math.floor(Math.random() * 4)) * 360;
          const targetSegmentCenter = selectedIndex * segmentAngle + segmentAngle / 2;
          const finalAngle = extraSpins + (360 - targetSegmentCenter);

          this.rotationDegree = finalAngle;

          setTimeout(() => {
            this.isSpinning = false;
            this.hasSpun = true;
            this.wonReward = selectedReward;

            if (typeof window !== 'undefined') {
              const storageKey = `hasSpunWheel_${this.campaign?._id || 'default'}`;
              localStorage.setItem(storageKey, 'true');
            }
          }, 4500);
        } else {
          this.isSpinning = false;
          const msg = res?.message || 'Unable to spin at this time.';
          this.snackBar.openErrorSnackBar(msg);
          if (msg.toLowerCase().includes('already') || res?.code === 4001) {
            this.alreadyUsed = true;
          }
        }
      },
      error: (err) => {
        this.isSpinning = false;
        this.submittingOutcome = false;
        const msg =
          err?.error?.message || err?.message || 'Failed to process spin. Please try again.';
        this.snackBar.openErrorSnackBar(msg);
        if (msg.toLowerCase().includes('already') || err?.status === 400) {
          this.alreadyUsed = true;
        }
      }
    });
  }

  getSVGPath(index: number): string {
    const total = this.rewards.length || 1;
    const angle = 360 / total;
    const startAngle = (index * angle - 90) * (Math.PI / 180);
    const endAngle = ((index + 1) * angle - 90) * (Math.PI / 180);
    const radius = 150;
    const cx = 150;
    const cy = 150;

    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);

    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} Z`;
  }

  getContentCenter(index: number): { x: number; y: number } {
    const total = this.rewards.length || 1;
    const angle = 360 / total;
    const midAngle = (index * angle + angle / 2 - 90) * (Math.PI / 180);
    const radius = 95;
    return {
      x: 150 + radius * Math.cos(midAngle),
      y: 150 + radius * Math.sin(midAngle)
    };
  }

  getDotCoordinates(index: number): { x: number; y: number } {
    const total = this.rewards.length || 1;
    const angle = 360 / total;
    const dotAngle = (index * angle - 90) * (Math.PI / 180);
    const radius = 145;
    return {
      x: 150 + radius * Math.cos(dotAngle),
      y: 150 + radius * Math.sin(dotAngle)
    };
  }

  copyCode(code: string): void {
    if (!code) return;
    navigator.clipboard.writeText(code);
    this.copied = true;
    setTimeout(() => (this.copied = false), 2000);
  }

  close(): void {
    if (typeof window !== 'undefined') {
      const storageKey = `hasSpunWheel_${this.campaign?._id || 'default'}`;
      localStorage.setItem(storageKey, 'true');
    }
    this.dialogRef.close(this.wonReward);
  }
}
